import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    IconButton,
    Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { McTable } from "@maersk-global/mds-react-wrapper/components-core/mc-table";

const maerskFontFamily = '"Maersk Text", -apple-system, BlinkMacSystemFont, "Microsoft JhengHei", "Microsoft Yahei", "微软雅黑", STXihei, "华文细黑", sans-serif';

const DependenciesTab = ({ changeData, setisdrawerOpen, setDrawerCi, setDrawerCiCategory, setDrawerType }) => {
    const affectedCis = changeData?.affected_cis || [];
    const upstreamApps = changeData?.upstream_apps || [];
    const downstreamApps = changeData?.downstream_apps || [];
    const upstreamChanges = changeData?.upstream_changes || [];
    const downstreamChanges = changeData?.downstream_changes || [];

    // Handle change number click - opens in new tab
    const handleChangeNumberClick = (changeNumber) => {
        if (changeNumber) {
            const url = `/change_details?changeNumber=${encodeURIComponent(changeNumber)}`;
            window.open(url, '_blank');
        }
    };

    // Handle CI info click - opens drawer (if drawer props are available)
    const handleCiInfoClick = (ciName, category = 'Application') => {
        if (setisdrawerOpen && setDrawerCi && setDrawerCiCategory && setDrawerType) {
            setTimeout(() => {
                setisdrawerOpen(true);
                setDrawerCi(ciName);
                setDrawerCiCategory(category);
                setDrawerType("ci_info");
            }, 0);
        }
    };

    // Transform affected CIs data for table
    const affectedCisTableData = affectedCis.map((ci, index) => ({
        id: `ci-${index}`,
        ciName: ci.ci_name || ci,
        className: ci.class_name || 'N/A',
        environment: ci.ci_environment || 'N/A'
    }));

    // Column definitions for Affected CIs table
    const affectedCisColumns = [
        { id: "ciName", label: "CI Name", sortable: true },
        { id: "className", label: "Class Name", sortable: true },
        { id: "environment", label: "Environment", sortable: true }
    ];

    // Transform upstream changes data for table
    const upstreamChangesTableData = upstreamChanges.map((change, index) => ({
        id: `upstream-${index}`,
        changeNumber: change.change_number || change.number || change,
        configurationItem: change.cmdb_ci || 'N/A',
        risk: change.risk || 'N/A',
        shortDescription: change.short_description || 'N/A',
        plannedStartDate: change.start_date || 'N/A',
        plannedEndDate: change.end_date || 'N/A',
        state: change.state || 'N/A',
        type: change.type || 'N/A',
        assignmentGroup: change.assignment_group || 'N/A',
        requestedBy: change.requested_by || 'N/A',
        closeCode: change.close_code || 'N/A'
    }));

    // Transform downstream changes data for table
    const downstreamChangesTableData = downstreamChanges.map((change, index) => ({
        id: `downstream-${index}`,
        changeNumber: change.change_number || change.number || change,
        configurationItem: change.cmdb_ci || 'N/A',
        risk: change.risk || 'N/A',
        shortDescription: change.short_description || 'N/A',
        plannedStartDate: change.start_date || 'N/A',
        plannedEndDate: change.end_date || 'N/A',
        state: change.state || 'N/A',
        type: change.type || 'N/A',
        assignmentGroup: change.assignment_group || 'N/A',
        requestedBy: change.requested_by || 'N/A',
        closeCode: change.close_code || 'N/A'
    }));

    // Column definitions for Changes tables (matching Table.jsx)
    const changesColumns = [
        { id: "changeNumber", label: "Change Number", sortable: true },
        { id: "configurationItem", label: "Configuration Item", sortable: true },
        { id: "risk", label: "Risk", sortable: true },
        { id: "shortDescription", label: "Short Description", sortable: true, nowrap: false },
        { id: "plannedStartDate", label: "Planned Start Date", sortable: true },
        { id: "plannedEndDate", label: "Planned End Date", sortable: true },
        { id: "state", label: "State", sortable: true },
        { id: "type", label: "Type", sortable: true },
        { id: "assignmentGroup", label: "Assignment Group", sortable: true },
        { id: "requestedBy", label: "Requested By", sortable: true },
        { id: "closeCode", label: "Close Code", sortable: true }
    ];

    return (
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Affected CIs Table */}
                <Card sx={{ mb: 3, border: '1px solid #d9d9d9', boxShadow: 'none', borderRadius: '10px' }}>
                    <CardContent sx={{ padding: '24px' }}>
                        <Typography variant="h6" gutterBottom sx={{ fontFamily: maerskFontFamily, color: '#333' }}>
                            Affected CIs
                        </Typography>
                        {affectedCisTableData.length > 0 ? (
                            <McTable
                                data={affectedCisTableData}
                                columns={affectedCisColumns}
                                datakey="id"
                                headersticky
                                fit="small"
                                customstyles=".mds-table { max-height: 400px !important; }"
                            >
                                {affectedCisTableData.map((row) => (
                                    <React.Fragment key={row.id}>
                                        <div slot={`${row.id}_ciName`}>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <span style={{ fontFamily: maerskFontFamily }}>
                                                    {row.ciName}
                                                </span>
                                            </div>
                                        </div>
                                        <div slot={`${row.id}_className`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.className}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_environment`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.environment}
                                            </span>
                                        </div>
                                    </React.Fragment>
                                ))}
                            </McTable>
                        ) : (
                            <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, color: '#666' }}>
                                No affected CIs found
                            </Typography>
                        )}
                    </CardContent>
                </Card>

                {/* Upstream Changes Table */}
                <Card sx={{ mb: 3, border: '1px solid #d9d9d9', boxShadow: 'none', borderRadius: '10px' }}>
                    <CardContent sx={{ padding: '24px' }}>
                        <Typography variant="h6" gutterBottom sx={{ fontFamily: maerskFontFamily, color: '#333' }}>
                            Upstream Changes
                        </Typography>
                        {upstreamChangesTableData.length > 0 ? (
                            <McTable
                                data={upstreamChangesTableData}
                                columns={changesColumns}
                                datakey="id"
                                headersticky
                                fit="small"
                                customstyles=".mds-table { max-height: 400px !important; }"
                            >
                                {upstreamChangesTableData.map((row) => (
                                    <React.Fragment key={row.id}>
                                        <div slot={`${row.id}_changeNumber`}>
                                            <span 
                                                onClick={() => handleChangeNumberClick(row.changeNumber)}
                                                style={{ 
                                                    fontFamily: maerskFontFamily, 
                                                    color: '#0077b2', 
                                                    cursor: 'pointer',
                                                    textDecoration: 'none'
                                                }}
                                            >
                                                {row.changeNumber}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_configurationItem`}>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <span style={{ fontFamily: maerskFontFamily }}>
                                                    {row.configurationItem}
                                                </span>
                                                {setisdrawerOpen && row.configurationItem !== 'N/A' && (
                                                    <Tooltip title="Show CI Info">
                                                        <IconButton
                                                            style={{
                                                                marginLeft: '0.2rem',
                                                                padding: 0,
                                                                verticalAlign: 'top'
                                                            }}
                                                            tabIndex={-1}
                                                            onClick={() => handleCiInfoClick(row.configurationItem, 'Application')}
                                                            aria-label="Show CI info"
                                                        >
                                                            <InfoOutlinedIcon fontSize="small" style={{ fontWeight: "normal" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </div>
                                        <div slot={`${row.id}_risk`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.risk}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_shortDescription`}>
                                            <span style={{ fontFamily: maerskFontFamily, wordBreak: 'break-word' }}>
                                                {row.shortDescription}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_plannedStartDate`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.plannedStartDate}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_plannedEndDate`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.plannedEndDate}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_state`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.state}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_type`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.type}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_assignmentGroup`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.assignmentGroup}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_requestedBy`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.requestedBy}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_closeCode`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.closeCode}
                                            </span>
                                        </div>
                                    </React.Fragment>
                                ))}
                            </McTable>
                        ) : (
                            <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, color: '#666' }}>
                                No upstream changes found
                            </Typography>
                        )}
                    </CardContent>
                </Card>

                {/* Downstream Changes Table */}
                <Card sx={{ mb: 3, border: '1px solid #d9d9d9', boxShadow: 'none', borderRadius: '10px' }}>
                    <CardContent sx={{ padding: '24px' }}>
                        <Typography variant="h6" gutterBottom sx={{ fontFamily: maerskFontFamily, color: '#333' }}>
                            Downstream Changes
                        </Typography>
                        {downstreamChangesTableData.length > 0 ? (
                            <McTable
                                data={downstreamChangesTableData}
                                columns={changesColumns}
                                datakey="id"
                                headersticky
                                fit="small"
                                customstyles=".mds-table { max-height: 400px !important; }"
                            >
                                {downstreamChangesTableData.map((row) => (
                                    <React.Fragment key={row.id}>
                                        <div slot={`${row.id}_changeNumber`}>
                                            <span 
                                                onClick={() => handleChangeNumberClick(row.changeNumber)}
                                                style={{ 
                                                    fontFamily: maerskFontFamily, 
                                                    color: '#0077b2', 
                                                    cursor: 'pointer',
                                                    textDecoration: 'none'
                                                }}
                                            >
                                                {row.changeNumber}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_configurationItem`}>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <span style={{ fontFamily: maerskFontFamily }}>
                                                    {row.configurationItem}
                                                </span>
                                                {setisdrawerOpen && row.configurationItem !== 'N/A' && (
                                                    <Tooltip title="Show CI Info">
                                                        <IconButton
                                                            style={{
                                                                marginLeft: '0.2rem',
                                                                padding: 0,
                                                                verticalAlign: 'top'
                                                            }}
                                                            tabIndex={-1}
                                                            onClick={() => handleCiInfoClick(row.configurationItem, 'Application')}
                                                            aria-label="Show CI info"
                                                        >
                                                            <InfoOutlinedIcon fontSize="small" style={{ fontWeight: "normal" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </div>
                                        <div slot={`${row.id}_risk`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.risk}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_shortDescription`}>
                                            <span style={{ fontFamily: maerskFontFamily, wordBreak: 'break-word' }}>
                                                {row.shortDescription}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_plannedStartDate`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.plannedStartDate}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_plannedEndDate`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.plannedEndDate}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_state`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.state}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_type`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.type}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_assignmentGroup`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.assignmentGroup}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_requestedBy`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.requestedBy}
                                            </span>
                                        </div>
                                        <div slot={`${row.id}_closeCode`}>
                                            <span style={{ fontFamily: maerskFontFamily }}>
                                                {row.closeCode}
                                            </span>
                                        </div>
                                    </React.Fragment>
                                ))}
                            </McTable>
                        ) : (
                            <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, color: '#666' }}>
                                No downstream changes found
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            </Box>

            <Box sx={{ width: { xs: '100%', md: '40%' }, flexShrink: 0 }}>
                {/* Upstream Applications */}
                <Card sx={{ mb: 3, border: '1px solid #d9d9d9', boxShadow: 'none', borderRadius: '10px' }}>
                    <CardContent sx={{ padding: '24px' }}>
                        <Typography variant="h6" gutterBottom sx={{ fontFamily: maerskFontFamily, color: '#333' }}>
                            Upstream Applications
                        </Typography>
                        <Box sx={{ 
                            maxHeight: '300px', 
                            overflowY: 'auto',
                            pr: 1,
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: '#f1f1f1',
                                borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: '#c1c1c1',
                                borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                                background: '#a1a1a1',
                            }
                        }}>
                            {upstreamApps.length > 0 ? (
                                <Box component="ul" sx={{ margin: 0, paddingLeft: '20px', listStyle: 'disc' }}>
                                    {upstreamApps.map((app, index) => (
                                        <Box component="li" key={index} sx={{ mb: 1 }}>
                                            <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                                                {app.name || app}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, color: '#666' }}>
                                    No upstream applications found
                                </Typography>
                            )}
                        </Box>
                    </CardContent>
                </Card>

                {/* Downstream Applications */}
                <Card sx={{ mb: 3, border: '1px solid #d9d9d9', boxShadow: 'none', borderRadius: '10px' }}>
                    <CardContent sx={{ padding: '24px' }}>
                        <Typography variant="h6" gutterBottom sx={{ fontFamily: maerskFontFamily, color: '#333' }}>
                            Downstream Applications
                        </Typography>
                        <Box sx={{ 
                            maxHeight: '300px', 
                            overflowY: 'auto',
                            pr: 1,
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: '#f1f1f1',
                                borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: '#c1c1c1',
                                borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                                background: '#a1a1a1',
                            }
                        }}>
                            {downstreamApps.length > 0 ? (
                                <Box component="ul" sx={{ margin: 0, paddingLeft: '20px', listStyle: 'disc' }}>
                                    {downstreamApps.map((app, index) => (
                                        <Box component="li" key={index} sx={{ mb: 1 }}>
                                            <Typography variant="body2" sx={{ fontFamily: maerskFontFamily }}>
                                                {app.name || app}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, color: '#666' }}>
                                    No downstream applications found
                                </Typography>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default DependenciesTab;
